import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import HttpRunner from '../query';
import urlHandler from '../../../utils/urlHandler';
import perms from '../../../utils/permissions';
import examples from '../../../utils/examples';

class DocV1Sentiment extends Component {
    constructor(props){
        super(props);

        this.state = {
            dynamicSample: false
        }
    }

    componentDidMount() {
        this.setState({
            dynamicSample: perms.checkEndpoint(urlHandler.API_ENDPOINT_V1_DOC_SENTIMENT)
        })
    }

    render() {
        return (
            <div className="bucket" id="top">
                <span>
                    <span className='heading-1'>Document Sentiment</span>
                </span>
                <code className="api-definition">
                    <span className="post-badge">POST</span>&nbsp;{urlHandler.API_ENDPOINT_V1_DOC_SENTIMENT}
                </code>
                <div className="description">
                    The Document Sentiment endpoint extracts sentiment from a piece of text. Sentiment is classified for the entire 
                    block of text as well as each sentence. The sentiment classifications used is dictated by 
                    the <a href="#sentiment_models">sentiment model</a> selected. Each sentiment value includes a corresponding confidence 
                    score which indicates our confidence that the 
                    sentiment is correct. For some <a href="#sentiment_models">sentiment models</a>, a sentiment score is also provided.
                </div>

                <h4 className="heading-2">Request</h4>
                <div className="description enrich">
                    A sentiment model and text block must be provided for sentiment analysis. Below is an example of a minimum document 
                    sentiment request.

                    <div className="example-content host">
                        <pre>
                            POST {urlHandler.API_ENDPOINT_V1_DOC_SENTIMENT}<br/>
                            Accept: application/json<br/>
                            Authorization: Bearer [YOUR-API-KEY]<br/>
                            Content-Type: application/json<br/>
                            <br/>
                        </pre>
                        <pre>{JSON.stringify(examples.EXAMPLE_REQUEST_V1_SENTIMENT1, null, 3)}</pre>
                    </div>
                    <HttpRunner example="sentiment2" 
                        query="doc-sentiment"
                        response={!this.state.dynamicSample ? JSON.stringify(examples.EXAMPLE_RESPONSE_V1_SENTIMENT1) : null}
                    />
                </div>

                <h4 className="heading-3">Request object</h4>
                
                <table className="table-wrapper">
                    <thead className="table-header">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Values </th>
                        <th>Default </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">documentId</td>
                        <td className="meaning">A client supplied tracking identifier.</td>
                        <td className="type">String</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">documentSentimentModel</td>
                        <td className="meaning">The sentiment model chosen for analysis.</td>
                        <td className="type">See <a href="#sentiment_models">sentiment models</a> for supported models.</td>
                        <td className="constraints">Required</td>
                    </tr>
                    <tr>
                        <td className="attribute">languageCode</td>
                        <td className="meaning">Language in which the text must be analyzed.<br/><br/>

                            Specified as an ISO 639-1 code.
                        </td>
                        <td className="type">"en": English</td>
                        <td className="constraints">Optional<br/><br/>
                            Default: "en"
                        </td>
                    </tr>
                    <tr>
                        <td className="attribute">text</td>
                        <td className="meaning">The UTF-8 block of text to be analyzed.</td>
                        <td className="type">String</td>
                        <td className="constraints">Required</td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="sentiment_models">Sentiment Models</h4>
                <div className="description enrich">
                    Multiple sentiment models are available to support several use cases. The chosen model will dictate 
                    which sentiment classes will be returned in the response.
                </div>

                <table className="table-wrapper">
                    <thead className="table-header">
                    <tr>
                        <th>Model Name </th>
                        <th>Model Description </th>
                        <th>Sentiment Classes </th>
                        <th>Score Supported </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">news3</td>
                        <td className="meaning">A 3-class sentiment model applicable to news and social media content. Available for languageCode: <i>en</i>.</td>
                        <td className="type">positive,neutral,<br/>negative</td>
                        <td className="type"><span className="fa fa-check"></span></td>
                    </tr>
                    <tr> 
                        <td className="attribute">earningsCall</td> 
                        <td className="meaning">A 3-class sentiment model applicable to earnings calls. Available for languageCode: <i>en</i>.</td> 
                        <td className="type">positive,neutral,<br/>negative</td> 
                        <td className="type"><span className="fa fa-check"></span></td> 
                    </tr>
                    <tr>
                        <td className="attribute">financial3</td>
                        <td className="meaning">A 3-class sentiment model applicable to financial content. Available for languageCode: <i>en</i>.</td>
                        <td className="type">positive,neutral,<br/>negative</td>
                        <td className="type"><span className="fa fa-check"></span></td>
                    </tr>
                    <tr>
                        <td className="attribute">financialTopics</td>
                        <td className="meaning">A 3-class sentiment model that combines topic modeling, with LDA, and 
                            deep transfer learning to achieve state-of-the-art results for document, sentence and entity 
                            level financial news sentiment analysis and event detection.<br/><br/>
                            This model produces a sentiment score value, predicted probabilities for each sentiment class, and a 
                            label for the dominant event or topic of the context.
                        </td>
                        <td className="type">positive,neutral,<br/>negative</td>
                        <td className="type"><span className="fa fa-check"></span></td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-2">Response</h4>
                <div className="description enrich">
                    The document sentiment response object contains a top-level sentiment object reporting the sentiment analysis for the 
                    entire block of text. An array of sentence sentiment objects reports the extracted sentence attributes and assigned 
                    sentiment class.<br/><br/>
                </div>

                <h4 className="heading-3">Response object</h4>

                <table className="table-wrapper">
                    <thead className="table-header">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Default </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">sentiment</td>
                        <td className="meaning">The top-level sentiment response.</td>
                        <td className="type">See the <a href="#sentiment_object">sentiment object</a> definition</td>
                        <td className="constraints">Required</td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="sentiment_object">Sentiment object</h4>

                <table className="table-wrapper">
                    <thead className="table-header">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Default </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">class</td>
                        <td className="meaning">The overall sentiment assigned to the document.</td>
                        <td className="type">String<br/><br/>

                            See the supported sentiment classes for the requested <a href="#sentiment_models" className="list-link">sentiment model</a> given in the request.
                        </td>
                        <td className="constraints">Required</td>
                    </tr>
                    <tr>
                        <td className="attribute">classConfidence</td>
                        <td className="meaning">The confidence associated with the sentiment class.</td>
                        <td className="type">Number <br/><br/>
                            Range: [0.0, 1.0]
                        </td>
                        <td className="constraints">Required</td>
                    </tr>
                    <tr>
                        <td className="attribute">score</td>
                        <td className="meaning">An optional sentiment strength expressed as a decimal number in the range -1.0 to 1.0, 
                            with 0.0 being neutral.<br/><br/>
                            
                            The selected <a href="#sentiment_models">sentiment model</a> will determine if this value is available.</td>
                        <td className="type">Number <br/><br/>
                            Range: [-1.0, 1.0]
                        </td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">probabilities</td>
                        <td className="meaning">An array of decimal numbers that represent the predicted probabilities for each sentiment class.<br/><br/>
                            
                            Only valid for <i>financialTopics</i> <a href="#sentiment_models">sentiment model</a>.</td>
                        <td className="type">Array of decimal numbers</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">sentences</td>
                        <td className="meaning">List of sentences into which the document text is divided.</td>
                        <td className="type">An array of <a href="#sentence_sentiment_object">sentence sentiment objects</a>.</td>
                        <td className="constraints">Required</td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-3" id="sentence_sentiment_object">Sentence Sentiment object</h4>

                <table className="table-wrapper">
                    <thead className="table-header">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Default </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">class</td>
                        <td className="meaning">The sentiment assigned to the sentence.</td>
                        <td className="type">String<br/><br/>

                            See the supported sentiment classes for the requested <a href="#sentiment_models" className="list-link">sentiment model</a>.
                        </td>
                        <td className="constraints">Required</td>
                    </tr>
                    <tr>
                        <td className="attribute">classConfidence</td>
                        <td className="meaning">The confidence associated with the sentiment class.</td>
                        <td className="type">Number<br/><br/>
                            Range: [0.0, 1.0]
                        </td>
                        <td className="constraints">Required</td>
                    </tr>
                    <tr>
                        <td className="attribute">position</td>
                        <td className="meaning">Two item list containing the start and end position of the sentence within the 
                            analyzed text block. Offsets are zero-based.</td>
                        <td className="type">Array of numbers</td>
                        <td className="constraints">Required</td>
                    </tr>
                    <tr>
                        <td className="attribute">score</td>
                        <td className="meaning">An optional sentiment strength expressed as a decimal number in the range -1.0 to 1.0 
                            with 0.0 being neutral.<br/><br/>

                            The selected <a href="#sentiment_models">sentiment model</a> will determine if this value is available.
                        </td>
                        <td className="type">Number<br/><br/>
                            Range: [-1.0, 1.0]
                        </td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">probabilities</td>
                        <td className="meaning">An array of decimal numbers that represent the predicted probabilities for each sentiment class.<br/><br/>
                            
                            Only valid for <i>financialTopics</i> <a href="#sentiment_models">sentiment model</a>.</td>
                        <td className="type">Array of decimal numbers</td>
                        <td className="constraints">Optional</td>
                    </tr>
                    <tr>
                        <td className="attribute">topic</td>
                        <td className="meaning">The label for the dominant event or topic of the context.<br/><br/>
                            
                            Only valid for <i>financialTopics</i> <a href="#sentiment_models">sentiment model</a>. See <Link to="/documentation/doc_v2enrich#financial_topics" className="list-link">topics</Link> for valid values.</td>
                        <td className="type">String</td>
                        <td className="constraints">Optional.</td>
                    </tr>
                    <tr>
                        <td className="attribute">text</td>
                        <td className="meaning">The text of the sentence.</td>
                        <td className="type">String</td>
                        <td className="constraints">Required</td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-2">Example</h4>
                <div className="example-content host">
                    <pre>{JSON.stringify(examples.EXAMPLE_REQUEST_V1_SENTIMENT2, null, 3)}</pre>
                </div>
                <HttpRunner example="sentiment1" 
                    query="doc-sentiment"
                    response={!this.state.dynamicSample ? JSON.stringify(examples.EXAMPLE_RESPONSE_V1_SENTIMENT2) : null}/>
            </div>
        );
    }
}

export default DocV1Sentiment;